/**
 * Core browser implementation.
 *
 * @author Stanislav Kalashnik <sk@infomir.eu>
 * @author Yaroslav Surilov <y.surilov@infomir.com>
 */

'use strict';

var app                 = require('stb-app'),
    keys                = require('stb-keys'),
    messages            = require('../../../../plugin-browser/src/js/messages'),
    messagesToEngine    = messages.toEngine,
    messagesToApp       = messages.toApp,
    tools               = require('./tools'),
    config              = require('../config'),
    core                = {},
    screenBoundsRegular = {
        x: app.metrics.availLeft,
        y: app.metrics.availTop * 3,
        w: app.metrics.width - app.metrics.availLeft - app.metrics.availRight,
        h: app.metrics.height - (app.metrics.availTop * 3) - (app.metrics.availBottom * 3)
    },
    screenBoundsFullscreen = {
        x: app.metrics.availLeft,
        y: app.metrics.availTop,
        w: app.metrics.width - app.metrics.availLeft - app.metrics.availRight,
        h: app.metrics.height - app.metrics.availTop - app.metrics.availBottom
    },
    loadProgress = 0;


core.storage               = {};
core.MODE_MENU             = 1;
core.MODE_BROWSER          = 2;
// active core state, starting mode is browser
core.mode                  = core.MODE_BROWSER;
core.webWindowHandle       = null;
core.webWindowIsFullScreen = false;
// 2D navigation
core.webWindowIsVMouse     = false;
core.webWindowZoom         = 100;
core.webWindowUrl          = null;
core.resourcesLoadState    = {
    isColdStart:          true,
    isLoadStartTriggered: false,
    browseIntent:         false
};


/** Activate user preferences. */
function activateUserPrefs () {
    core.actions.frame(core.webWindowIsFullScreen);
    core.actions.f2(core.webWindowIsVMouse);
    core.actions.scale(core.webWindowZoom);
}


/**
 * Receive/route/handle data from plugin.
 *
 * @param {number} from - sender's ID
 * @param {string} message - message
 * @param {string} data - transmitted data
 */
stbEvent.onMessage = function ( from, message, data ) {
    switch ( message ) {
        case messagesToEngine.userSettings:
            core.storage = JSON.parse(data);

            core.webWindowZoom = core.storage.settings.scale;
            core.webWindowIsFullScreen = core.storage.settings.isFullScreen;
            core.webWindowIsVMouse     = core.storage.settings.isVirtualMouse;

            if ( core.resourcesLoadState.isColdStart ) {
                core.resourcesLoadState.isColdStart = false;
                activateUserPrefs();
            }

            break;
        case messagesToEngine.reload:
            core.actions.f1();
            break;
        case messagesToEngine.stopLoading:
            core.actions.stop();
            break;
        case messagesToEngine.navigateForward:
            core.actions.goForward();
            break;
        case messagesToEngine.navigateBackward:
            core.actions.goBack();
            break;
        case messagesToEngine.userTextInput:
            core.webWindowShow(true);
            core.loadUrl(data);
            break;
        case messagesToEngine.showWebWindow:
            core.mode = core.MODE_BROWSER;
            core.webWindowShow(true);

            if ( data ) {
                core.loadUrl(data);
            } else if ( loadProgress < 100 ) {
                core.loadUrl(core.webWindowUrl);
            }

            break;
        case messagesToEngine.hideWebWindow:
            app.emit('keydown', {code: keys.menu});
            break;
        case messagesToEngine.openVirtualKeyboard:
            gSTB.IsVirtualKeyboardActive() ? gSTB.HideVirtualKeyboard() : gSTB.ShowVirtualKeyboard(); // eslint-disable-line
            break;
        case messagesToEngine.search:
            app.emit('keydown', {code: keys.f4});
            break;
        case messagesToEngine.setFullScreen:
            app.emit('keydown', {code: keys.frame});
            break;
        case messagesToEngine.intentUrl:
            core.resourcesLoadState.browseIntent = true;
            core.webWindowShow(true);
            tools.validateUrl(data) && core.loadUrl(data);
            break;
    }
};

stbEvent.bind('load.start', function ( url ) {
    if ( core.webWindowUrl !== url ) {
        stbWebWindow.messageSend(
            config.portalWindowId,
            messagesToApp.changeUrl,
            JSON.stringify({
                url:   url,
                title: stbWindowMgr.getCurrentTitle()
            })
        );
        core.webWindowUrl = url;
    }

    stbWebWindow.messageSend(config.portalWindowId, messagesToApp.unsetFavicon);
    core.resourcesLoadState.browseIntent && stbWebWindow.messageSend(config.portalWindowId, messagesToApp.reload, core.webWindowUrl);
});

stbEvent.bind('load.finish', function ( status ) {
    stbWebWindow.messageSend(config.portalWindowId, messagesToApp.setFavicon);

    // on HostNotFoundError
    if ( status === 3 ) {
        // exit full-screen mode if any
        core.setWebWindowFullScreen(false);
        stbWebWindow.messageSend(config.portalWindowId, messagesToApp.notification, 'This webpage is not available');
    }
});

// global event on page loading change
stbEvent.onWebBrowserProgress = function ( progress, status ) {
    if ( !core.resourcesLoadState.isLoadStartTriggered ) {
        stbEvent.trigger('load.start', stbWindowMgr.getCurrWebUrl());
        core.resourcesLoadState.isLoadStartTriggered = true;
    }

    stbWebWindow.messageSend(config.portalWindowId, messagesToApp.setLoadProgress, '' + progress);
    loadProgress = progress;

    if ( progress === 100 ) {
        stbEvent.trigger('load.finish', status);
        core.resourcesLoadState.isLoadStartTriggered = false;
    }
};

stbEvent.onMediaAvailable = function ( itemMime, itemLink ) {
    core.mode = core.MODE_MENU;
    stbWindowMgr.windowHide(core.webWindowHandle);
    stbWebWindow.messageSend(config.portalWindowId, messagesToApp.openMenu);

    stbWebWindow.messageSend(
        config.portalWindowId,
        messagesToApp.mediaAvailable,
        JSON.stringify({
            itemMime: itemMime,
            itemLink: itemLink,
            itemName: require('cjs-uri').parse(itemLink).file
        })
    );
};

// global event on switch from full-screen mode
stbEvent.onWebBrowserFullScreenExit = function () {
    core.actions.frame();
};


/**
 * Load particular URL in Wild Web window.
 *
 * @param {string} url - URL to load
 */
core.loadUrl = function ( url ) {
    if ( core.webWindowHandle ) {
        stbWindowMgr.LoadUrl(url);
        activateUserPrefs();
    } else {
        core.webWindowHandle = stbWindowMgr.initWebWindow(url);
        stbWindowMgr.resizeWebWindow(screenBoundsRegular.x, screenBoundsRegular.y, screenBoundsRegular.w, screenBoundsRegular.h);
        stbWindowMgr.raiseWebWindow();
        stbWebWindow.messageSend(config.portalWindowId, messagesToApp.ready);
    }
};


/**
 * Global navigation key actions.
 *
 * @namespace
 */
core.actions = {
    // reload document
    f1: function () {
        stbWebWindow.ReloadDocument();
    },
    /**
     * Use virtual mouse.
     *
     * @param {boolean} state - state to set
     */
    f2: function ( state ) {
        if ( state === undefined ) {
            core.webWindowIsVMouse = !core.webWindowIsVMouse;
            stbWindowMgr.setVirtualMouseMode(core.webWindowIsVMouse);
        } else {
            stbWindowMgr.setVirtualMouseMode(state);
        }
    },
    // bookmarks
    f3: function () {
        // exit full-screen mode if any
        core.setWebWindowFullScreen(false);
        stbWebWindow.messageSend(
            config.portalWindowId,
            messagesToApp.favoritesState,
            JSON.stringify({
                url:   core.webWindowUrl,
                title: stbWindowMgr.getCurrentTitle()
            })
        );
    },
    // init magcore keyboard
    f4: function () {
        // exit full screen if necessary
        core.setWebWindowFullScreen(false);
        stbWindowMgr.windowHide(core.webWindowHandle);
        stbWebWindow.messageSend(config.portalWindowId, messagesToApp.search);
    },
    /**
     * Set full screen state.
     *
     * @param {boolean} state - state to set
     */
    frame: function ( state ) {
        if ( state === undefined ) {
            core.setWebWindowFullScreen(!core.webWindowIsFullScreen);
        } else {
            core.webWindowIsFullScreen = !state;
            core.setWebWindowFullScreen(state);
        }
    },
    // step back in browsing history
    goBack: function () {
        stbWebWindow.NavigateBack();
    },
    // step forward in browsing history
    goForward: function () {
        stbWebWindow.NavigateForward();
    },
    // stop loading web document
    stop: function () {
        stbWebWindow.StopLoading();
    },
    scale: function ( ratio ) {
        stbWebWindow.SetZoomFactor(ratio);
    }
};


core.webWindowShow = function () {
    if ( core.webWindowHandle !== null ) {
        gSTB.EnableVKButton(true);
        stbWindowMgr.windowShow(core.webWindowHandle);
        activateUserPrefs();
    }
};


core.setWebWindowFullScreen = function ( state ) {
    var geometry;

    // web window was initialized
    if ( core.webWindowHandle !== null ) {
        // new and old states are different
        if ( core.webWindowIsFullScreen !== state ) {
            geometry = state ? screenBoundsFullscreen : screenBoundsRegular;
            core.webWindowIsFullScreen = state;
            stbWindowMgr.resizeWebWindow(geometry.x, geometry.y, geometry.w, geometry.h);
            stbWebWindow.SetFullScreenMode(state);
        }
    }
};


module.exports = core;
